import React, { useState, useEffect, useCallback } from 'react';
import { getDepenses, getDepense, createDepense, updateDepense, deleteDepense } from '../api';
import ConfirmModal from './ConfirmModal';

const CATEGORIES = [
  'Fournitures de bureau',
  'Frais de déplacement',
  'Hébergement',
  'Restauration',
  'Téléphonie / Internet',
  'Logiciels / Abonnements',
  'Loyer / Charges',
  'Formation',
  'Matériel informatique',
  'Sous-traitance',
  'Autres',
];

const TAUX_TVA_OPTIONS = [
  { value: 0, label: '0 %' },
  { value: 2.1, label: '2,1 %' },
  { value: 5.5, label: '5,5 %' },
  { value: 10, label: '10 %' },
  { value: 20, label: '20 %' },
];

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_LIGNE = () => ({
  description: '',
  categorie: '',
  montant_ht: '',
  taux_tva: 20,
  montant_ttc: '',
});

const EMPTY_FORM = () => ({
  type: 'depense',
  libelle: '',
  fournisseur: '',
  date_depense: today(),
  reference: '',
  notes: '',
  lignes: [EMPTY_LIGNE()],
});

function computeTtc(ht, tva) {
  const h = parseFloat(ht) || 0;
  const t = parseFloat(tva) || 0;
  return (h * (1 + t / 100)).toFixed(2);
}

function Depenses() {
  const [depenses, setDepenses] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM());
  const [alert, setAlert] = useState(null);
  const [working, setWorking] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [filters, setFilters] = useState({ type: '', date_debut: '', date_fin: '' });

  const loadAll = useCallback(async () => {
    try {
      const params = {};
      if (filters.type) params.type = filters.type;
      if (filters.date_debut) params.date_debut = filters.date_debut;
      if (filters.date_fin) params.date_fin = filters.date_fin;
      const res = await getDepenses(params);
      setDepenses(res.data);
    } catch {
      showAlert('Erreur lors du chargement', 'error');
    }
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const showAlert = (message, type) => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 4000);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(EMPTY_FORM());
  };

  const openNew = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM());
    setShowModal(true);
  };

  const openEdit = async (d) => {
    try {
      const res = await getDepense(d.id);
      const { lignes, ...header } = res.data;
      setEditingId(d.id);
      setFormData({
        type: header.type || 'depense',
        libelle: header.libelle || '',
        fournisseur: header.fournisseur || '',
        date_depense: header.date_depense || today(),
        reference: header.reference || '',
        notes: header.notes || '',
        lignes: lignes && lignes.length > 0
          ? lignes.map(l => ({
              description: l.description || '',
              categorie: l.categorie || '',
              montant_ht: l.montant_ht != null ? String(l.montant_ht) : '',
              taux_tva: l.taux_tva != null ? l.taux_tva : 20,
              montant_ttc: l.montant_ttc != null ? String(l.montant_ttc) : '',
            }))
          : [EMPTY_LIGNE()],
      });
      setShowModal(true);
    } catch {
      showAlert('Erreur lors du chargement de la dépense', 'error');
    }
  };

  // --- Gestion des lignes ---

  const updateLigne = (idx, field, value) => {
    const lignes = formData.lignes.map((l, i) => {
      if (i !== idx) return l;
      const updated = { ...l, [field]: value };
      if (field === 'montant_ht' || field === 'taux_tva') {
        const ht = field === 'montant_ht' ? value : l.montant_ht;
        const tva = field === 'taux_tva' ? value : l.taux_tva;
        updated.montant_ttc = computeTtc(ht, tva);
      }
      return updated;
    });
    setFormData({ ...formData, lignes });
  };

  const addLigne = () => {
    setFormData({ ...formData, lignes: [...formData.lignes, EMPTY_LIGNE()] });
  };

  const removeLigne = (idx) => {
    if (formData.lignes.length <= 1) return;
    setFormData({ ...formData, lignes: formData.lignes.filter((_, i) => i !== idx) });
  };

  const totalHt = formData.lignes.reduce((s, l) => s + (parseFloat(l.montant_ht) || 0), 0);
  const totalTtc = formData.lignes.reduce((s, l) => s + (parseFloat(l.montant_ttc) || 0), 0);

  // --- Soumission ---

  const handleSubmit = async (e) => {
    e.preventDefault();
    setWorking(true);
    try {
      const payload = {
        type: formData.type,
        libelle: formData.libelle,
        fournisseur: formData.fournisseur,
        date_depense: formData.date_depense,
        reference: formData.reference,
        notes: formData.notes,
        lignes: formData.lignes.map(l => ({
          description: l.description,
          categorie: l.categorie,
          montant_ht: parseFloat(l.montant_ht) || 0,
          taux_tva: parseFloat(l.taux_tva) || 20,
          montant_ttc: parseFloat(l.montant_ttc) || 0,
        })),
      };
      if (editingId) {
        await updateDepense(editingId, payload);
        showAlert('Dépense modifiée', 'success');
      } else {
        await createDepense(payload);
        showAlert('Dépense créée', 'success');
      }
      closeModal();
      loadAll();
    } catch (err) {
      showAlert(err.response?.data?.error || 'Erreur lors de la sauvegarde', 'error');
    } finally {
      setWorking(false);
    }
  };

  const handleDelete = (d) => {
    setConfirm({
      title: 'Supprimer cette dépense',
      message: `Supprimer « ${d.libelle} » (${Number(d.total_ttc_lignes ?? d.montant_ttc).toFixed(2)} €) ? Cette action est irréversible.`,
      confirmLabel: 'Supprimer',
      variant: 'danger',
      onConfirm: async () => {
        setConfirm(null);
        try {
          await deleteDepense(d.id);
          showAlert('Dépense supprimée', 'success');
          loadAll();
        } catch (err) {
          showAlert(err.response?.data?.error || 'Erreur lors de la suppression', 'error');
        }
      },
    });
  };

  const grandTotalTtc = depenses.reduce((s, d) => s + (d.total_ttc_lignes ?? d.montant_ttc ?? 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>Dépenses & notes de frais</h2>
        <button className="btn btn-primary" onClick={openNew}>+ Nouvelle dépense</button>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type === 'error' ? 'error' : 'success'}`}>
          {alert.message}
        </div>
      )}

      {/* Filtres */}
      <div className="card" style={{ marginBottom: 16, padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 160 }}>
            <label>Type</label>
            <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
              <option value="">Tous</option>
              <option value="depense">Dépense</option>
              <option value="note_frais">Note de frais</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Du</label>
            <input type="date" value={filters.date_debut} onChange={(e) => setFilters({ ...filters, date_debut: e.target.value })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label>Au</label>
            <input type="date" value={filters.date_fin} onChange={(e) => setFilters({ ...filters, date_fin: e.target.value })} />
          </div>
          <button className="btn btn-secondary" onClick={() => setFilters({ type: '', date_debut: '', date_fin: '' })}>
            Réinitialiser
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Libellé</th>
              <th>Fournisseur</th>
              <th style={{ textAlign: 'center' }}>Lignes</th>
              <th style={{ textAlign: 'right' }}>Total HT</th>
              <th style={{ textAlign: 'right' }}>Total TTC</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {depenses.length === 0 ? (
              <tr><td colSpan="8" style={{ textAlign: 'center' }}>Aucune dépense</td></tr>
            ) : depenses.map(d => (
              <tr key={d.id}>
                <td>{new Date(d.date_depense + 'T00:00:00').toLocaleDateString('fr-FR')}</td>
                <td>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 10,
                    background: d.type === 'note_frais' ? 'var(--color-info-bg, #eff6ff)' : 'var(--color-neutral-bg, #f1f5f9)',
                    color: d.type === 'note_frais' ? 'var(--color-primary)' : 'var(--text-secondary)',
                  }}>
                    {d.type === 'note_frais' ? 'Note de frais' : 'Dépense'}
                  </span>
                </td>
                <td style={{ fontWeight: 500 }}>{d.libelle}</td>
                <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{d.fournisseur || '—'}</td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 10,
                    background: 'var(--color-neutral-bg, #f1f5f9)', color: 'var(--text-secondary)',
                  }}>
                    {d.nb_lignes ?? 1}
                  </span>
                </td>
                <td className="amount">{Number(d.total_ht_lignes ?? d.montant_ht).toFixed(2)} €</td>
                <td className="amount" style={{ fontWeight: 600 }}>{Number(d.total_ttc_lignes ?? d.montant_ttc).toFixed(2)} €</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => openEdit(d)}>Modifier</button>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(d)}>Supprimer</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {depenses.length > 0 && (
            <tfoot>
              <tr>
                <td colSpan="6" style={{ textAlign: 'right', fontWeight: 600, paddingRight: 12 }}>Total TTC</td>
                <td className="amount" style={{ fontWeight: 700, fontSize: 15 }}>{grandTotalTtc.toFixed(2)} €</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Modal création / édition */}
      {showModal && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 780 }}>
            <div className="modal-header">
              <h2>{editingId ? 'Modifier la dépense' : 'Nouvelle dépense'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              {/* En-tête de la note */}
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Type *</label>
                  <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })}>
                    <option value="depense">Dépense</option>
                    <option value="note_frais">Note de frais</option>
                  </select>
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Date *</label>
                  <input type="date" required value={formData.date_depense}
                    onChange={(e) => setFormData({ ...formData, date_depense: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <div className="form-group" style={{ flex: 2 }}>
                  <label>Libellé *</label>
                  <input type="text" required value={formData.libelle} placeholder="Ex : Note de frais déplacement Paris"
                    onChange={(e) => setFormData({ ...formData, libelle: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Fournisseur</label>
                  <input type="text" value={formData.fournisseur} placeholder="Nom du fournisseur"
                    onChange={(e) => setFormData({ ...formData, fournisseur: e.target.value })} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label>Référence / N° pièce</label>
                  <input type="text" value={formData.reference} placeholder="N° facture, ticket…"
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })} />
                </div>
              </div>

              {/* Tableau des lignes */}
              <div style={{ marginTop: 4, marginBottom: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>Lignes de dépense</span>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={addLigne}>
                    + Ajouter une ligne
                  </button>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--color-neutral-bg, #f8fafc)' }}>
                      <th style={thStyle}>Description</th>
                      <th style={{ ...thStyle, width: 160 }}>Catégorie</th>
                      <th style={{ ...thStyle, width: 100, textAlign: 'right' }}>Montant HT</th>
                      <th style={{ ...thStyle, width: 90, textAlign: 'center' }}>TVA</th>
                      <th style={{ ...thStyle, width: 100, textAlign: 'right' }}>Montant TTC</th>
                      <th style={{ ...thStyle, width: 36 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.lignes.map((l, idx) => (
                      <tr key={idx}>
                        <td style={tdStyle}>
                          <input
                            type="text"
                            value={l.description}
                            placeholder="Ex : Repas client"
                            onChange={(e) => updateLigne(idx, 'description', e.target.value)}
                            style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, outline: 'none', padding: '4px 2px' }}
                          />
                        </td>
                        <td style={tdStyle}>
                          <select
                            value={l.categorie}
                            onChange={(e) => updateLigne(idx, 'categorie', e.target.value)}
                            style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, outline: 'none', padding: '4px 2px' }}
                          >
                            <option value="">— Catégorie —</option>
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <input
                            type="number" step="0.01" min="0"
                            value={l.montant_ht}
                            onChange={(e) => updateLigne(idx, 'montant_ht', e.target.value)}
                            style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, textAlign: 'right', outline: 'none', padding: '4px 2px' }}
                          />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <select
                            value={String(l.taux_tva)}
                            onChange={(e) => updateLigne(idx, 'taux_tva', parseFloat(e.target.value))}
                            style={{ border: 'none', background: 'transparent', fontSize: 13, outline: 'none', padding: '4px 2px' }}
                          >
                            {TAUX_TVA_OPTIONS.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                          </select>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>
                          <input
                            type="number" step="0.01" min="0" required
                            value={l.montant_ttc}
                            onChange={(e) => updateLigne(idx, 'montant_ttc', e.target.value)}
                            style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 13, textAlign: 'right', outline: 'none', padding: '4px 2px' }}
                          />
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          {formData.lignes.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeLigne(idx)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger, #ef4444)', fontSize: 16, lineHeight: 1, padding: '2px 4px' }}
                              title="Supprimer la ligne"
                            >
                              ×
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* Totaux récap */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 24, marginTop: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
                  <span>Total HT : <strong style={{ color: 'var(--text-primary)' }}>{totalHt.toFixed(2)} €</strong></span>
                  <span>Total TTC : <strong style={{ color: 'var(--color-primary)', fontSize: 15 }}>{totalTtc.toFixed(2)} €</strong></span>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: 12 }}>
                <label>Notes</label>
                <textarea value={formData.notes} rows={2}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  style={{ width: '100%', resize: 'vertical' }} />
              </div>

              <div className="actions">
                <button type="submit" className="btn btn-primary" disabled={working}>
                  {working ? 'Sauvegarde…' : (editingId ? 'Sauvegarder' : 'Créer')}
                </button>
                <button type="button" className="btn btn-secondary" onClick={closeModal}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        message={confirm?.message}
        confirmLabel={confirm?.confirmLabel}
        variant={confirm?.variant || 'danger'}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const thStyle = {
  padding: '6px 8px',
  textAlign: 'left',
  fontWeight: 600,
  fontSize: 12,
  color: 'var(--text-secondary)',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
};

const tdStyle = {
  padding: '4px 8px',
  borderBottom: '1px solid var(--border-color, #e2e8f0)',
  verticalAlign: 'middle',
};

export default Depenses;
